/**
 * Otto Agent — client-side CRM data tools.
 *
 * The agentic gateway (`/agent/act`) decides WHICH data Otto needs and emits a
 * `tool_call`; the browser runs the matching function HERE, against crm-api,
 * and feeds the result back. Running client-side is deliberate: the browser
 * already holds the scoped `xop_crm_token`, so crm-api enforces the financial /
 * reseller / view-scope walls automatically — Otto sees exactly what the human
 * would. Everything here is READ-ONLY (see the `OTTO_DATA_TOOLS` allowlist).
 *
 * Lives inside the walled CRM sub-app so it can reuse the CRM axios client +
 * typed API modules; the parent agent surface imports it via
 * `@crm-src/lib/otto-tools`. The provider calls {@link ensureCrmSession} once
 * when a run starts (this surface is outside the `/crm` runtime); per-call 401s
 * then self-heal via the api interceptor.
 */
import type { OttoDataToolName } from '@xop/dashboard-shared';

import api, { ensureCrmSession } from './api';
import { ContactListsAPI } from './contact-lists-api';
import { ContactsAPI } from './contacts-api';
import { dealsApi } from './deals-api';
import { productsApi } from './products-api';

export { ensureCrmSession };

/** Cap on rows returned to the model — keeps the tool result (and token spend)
 * bounded while still giving Otto enough to disambiguate. */
const MAX_ROWS = 20;

/* ── result shapes (compact projections the LLM reasons over) ───────────── */

export interface OttoCustomerCandidate {
  customer_id: number;
  business_id: string | null;
  name: string;
  company: string | null;
  email: string | null;
}
export interface OttoContactRow {
  contact_id: number;
  name: string;
  email: string | null;
  job_title: string | null;
}
export interface OttoDealRow {
  deal_id: number;
  name: string;
  amount: number | null;
}
export interface OttoProductRow {
  part_number: string;
  description: string;
  product_id?: number;
  feature_id?: number;
  version_id?: number;
}
export interface OttoGroupRow {
  group_id: number;
  name: string;
  count: number;
}
export interface OttoDirectoryContactRow {
  contact_id: number;
  name: string;
  email: string | null;
  company: string | null;
}

function toNum(v: unknown): number | null {
  const n = typeof v === 'string' ? Number(v) : (v as number);
  return Number.isFinite(n) ? n : null;
}

/** Resolve a customer by fuzzy name/company. */
export async function findCustomer(query: string): Promise<{ candidates: OttoCustomerCandidate[] }> {
  const res = await api.get('/customers', {
    params: { search: query, per_page: MAX_ROWS, include_deals: false },
  });
  const rows: any[] = res.data?.data ?? res.data?.customers ?? [];
  return {
    candidates: rows.slice(0, MAX_ROWS).map((c) => ({
      customer_id: Number(c.id),
      business_id: c.customer_id ?? null,
      name: c.full_name ?? c.name ?? '',
      company: c.company_name ?? null,
      email: c.email ?? null,
    })),
  };
}

/** List a customer's own contacts (the bill-to/ship-to recipients). */
export async function listCustomerContacts(customerId: number): Promise<{ contacts: OttoContactRow[] }> {
  const res = await api.get(`/customers/${customerId}`);
  const customer = res.data?.data ?? res.data?.customer ?? res.data ?? {};
  const rows: any[] = customer.customer_contacts ?? customer.contacts ?? [];
  return {
    contacts: rows.slice(0, MAX_ROWS).map((c) => ({
      contact_id: Number(c.id),
      name: c.full_name ?? c.display_name ?? [c.first_name, c.last_name].filter(Boolean).join(' '),
      email: c.email ?? null,
      job_title: c.job_title ?? null,
    })),
  };
}

/** List a customer's deals (an invoice/quote is built against one deal). */
export async function getCustomerDeals(customerId: number): Promise<{ deals: OttoDealRow[] }> {
  const res = await dealsApi.getDeals({ customer_id: String(customerId), per_page: MAX_ROWS });
  const rows = res.customer_deals ?? [];
  return {
    deals: rows.slice(0, MAX_ROWS).map((d) => ({
      deal_id: Number(d.id),
      name: d.title || d.name || `Deal ${d.id}`,
      amount: toNum(d.amount),
    })),
  };
}

/**
 * Search the product catalog for the part-number(s) to quote against
 * (part-number + description). Products and their FEATURES — which is the
 * whole catalogue, since features lost their version level on 2026-09-13 and
 * a line item quotes a feature.
 */
export async function searchProducts(query: string): Promise<{ products: OttoProductRow[] }> {
  const h = await productsApi.getProductHierarchy();
  const q = query.trim().toLowerCase();
  const out: OttoProductRow[] = [];
  const match = (s?: string) => !q || (s ?? '').toLowerCase().includes(q);

  for (const p of h.products) {
    const pn = (p as any).part_number ?? p.code ?? String(p.id);
    if (match(p.name) || match(pn) || match((p as any).description)) {
      out.push({ part_number: pn, description: (p as any).description ?? p.name, product_id: p.id });
    }
  }
  for (const feats of Object.values(h.productFeatures)) {
    for (const f of feats as any[]) {
      if (match(f.name) || match(f.part_number) || match(f.description)) {
        out.push({
          part_number: f.part_number ?? f.code ?? String(f.id),
          description: f.description ?? f.name,
          product_id: f.product_id,
          feature_id: f.id,
        });
      }
    }
  }
  return { products: out.slice(0, MAX_ROWS) };
}

/** List contact groups (campaign audience segments). */
export async function listContactGroups(): Promise<{ groups: OttoGroupRow[] }> {
  const res = await ContactListsAPI.getAll({ per_page: MAX_ROWS } as never);
  const rows: any[] = (res as any)?.data ?? [];
  return {
    groups: rows.slice(0, MAX_ROWS).map((g) => ({
      group_id: Number(g.id),
      name: g.name,
      count: g.members_count ?? g.contacts_count ?? 0,
    })),
  };
}

/** Search the contacts directory (campaign recipients). */
export async function searchContacts(query: string): Promise<{ contacts: OttoDirectoryContactRow[] }> {
  const res = await ContactsAPI.getContacts({ search: query, per_page: MAX_ROWS } as never);
  const rows: any[] = (res as any)?.data ?? [];
  return {
    contacts: rows.slice(0, MAX_ROWS).map((c) => ({
      contact_id: Number(c.id),
      name: c.full_name ?? c.display_name ?? c.name ?? '',
      email: c.email ?? null,
      company: c.company ?? null,
    })),
  };
}

/**
 * Execute one data tool by name (the gateway `tool_call.name` + `input`).
 * Returns a JSON-serializable result the client appends to the transcript as a
 * `tool_result`. Throws on an unknown tool or a bad-shaped input so the caller
 * can surface an error rather than silently no-op.
 */
export async function runOttoDataTool(
  name: OttoDataToolName,
  input: Record<string, unknown>,
): Promise<unknown> {
  switch (name) {
    case 'find_customer':
      return findCustomer(String(input.query ?? ''));
    case 'list_customer_contacts':
      return listCustomerContacts(Number(input.customer_id));
    case 'get_customer_deals':
      return getCustomerDeals(Number(input.customer_id));
    case 'search_products':
      return searchProducts(String(input.query ?? ''));
    case 'list_contact_groups':
      return listContactGroups();
    case 'search_contacts':
      return searchContacts(String(input.query ?? ''));
    default: {
      const exhaustive: never = name;
      throw new Error(`Unknown Otto data tool: ${String(exhaustive)}`);
    }
  }
}
